<template>
  <loader v-if="loading" :stretch="true" :inverse="true" />
  <template v-else>
    <StudentChoiceHeader :style="themeStyle" />
    <div class="application-page" :style="themeStyle" :class="{ w100: loanConfiguration }">
      <input type="hidden" id="lid" name="lid" :value="lid" />
      <input type="hidden" id="productTypeId" name="productTypeId" :value="productTypeId" />
      <input type="hidden" id="programTypeId" name="programTypeId" :value="programTypeId" />
      <input type="hidden" id="leadSourceId" name="leadSourceId" :value="leadSourceId" />
      <input type="hidden" id="applicationTypeId" name="applicationTypeId" :value="applicationTypeId" />
      <div class="application-ad">
        <h1 v-if="error">Something has gone wrong.</h1>
        <slot v-else name="ad"></slot>
      </div>
      <div v-if="error" class="error-message">
        <p>
          We apologize, but we are not able to process your request at this time. If you continue to receive this
          message please contact support.
        </p>
      </div>
      <slot v-else></slot>
    </div>
    <div v-if="lid > 0" class="support-wrapper" :style="themeStyle">
      <a title="Support" @click="showSupportModal = true" href="#" class="support">
        <font-awesome-layers>
          <font-awesome-icon icon="fa-solid fa-circle" class="circle" />
          <font-awesome-icon
            icon="fa-solid fa-headset"
            transform="shrink-6"
            class="icon"
            :style="{ color: 'white' }"
          /> </font-awesome-layers
      ></a>
    </div>
    <transition name="modal">
      <support-modal :style="themeStyle" id="supportModal" @close="showSupportModal = false" v-if="showSupportModal" />
    </transition>
    <div class="application-page-footer" :style="themeStyle">
      <StudentChoiceFooter :external-links="externalLinks" :lender-id="lenderData?.lid" />
    </div>
  </template>
</template>
<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import useFormatter from "@/use/formatter";
import useApplication from "@/use/application";
import { useRoute, useRouter } from "vue-router";
import { $api } from "@/services/api";
import Loader from "@/components/shared/Loader.vue";
import StudentChoiceFooter from "@/components/shared/layout/FooterRefi.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import SupportModal from "@/components/modals/SupportModal.vue";
import type { AppData } from "@/models/application";
import StudentChoiceHeader from "@/components/shared/layout/Header.vue";
import { ProductIds } from "@/config/constants";
import useAuthentication from "@/use/authentication";
import { getCookie } from "@/composables/useCookies";
import useApplicationData from "@/composables/application/useApplicationData";

const props = defineProps({
  externalLinks: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { flowType } = useApplicationData();

const { formatHexToRgb } = useFormatter();
const { getLenderConfigurationAsync } = useApplication();
const { handleLoginRedirect, handleSilentSignOut } = useAuthentication();

const showSupportModal = ref<boolean>(false);
const error = ref<boolean>(false);
const loading = ref<boolean>(true);

const lid = computed<number>(() => store.state?.lenderData?.lid || route.query?.lid || 0);

const productTypeId = computed<number>(
  () => store.state.appData?.application?.productTypeId || route.query?.productTypeId || 0,
);

const programTypeId = computed<number>(
  () => store.state.appData?.application?.programTypeId || route.query?.programTypeId || 0,
);

const currentState = computed(() => store.state.appData?.currentState);

const loanConfiguration = computed(() => {
  if (
    currentState.value &&
    currentState.value?.name === "Loan Configuration" &&
    productTypeId.value === ProductIds.PEL &&
    loanConfigurationSubStep.value === 3
  ) {
    return true;
  }

  return false;
});

const loanConfigurationSubStep = computed<number>(() => store.state.loanConfigurationModule.currentSubStep);

const leadSourceId = computed(() => localStorage.getItem("leadSourceId") || route.query?.leadSourceId || 1);
const applicationTypeId = computed(() => {
  if (store?.state?.appData?.application?.metadata?.withCoApplicants !== undefined) {
    return store?.state?.appData?.application?.metadata?.withCoApplicants === true ? 2 : 1;
  } else {
    return 0;
  }
});
const lenderData = computed(() => store.state.lenderData);
const themeStyle = computed(() => {
  const defaultPrimaryColor = "#f7941e";
  const defaultSecondaryColor = "#ffffff";
  // SA color: #f7941e
  // Default Green: #01675a
  const themeRgb = formatHexToRgb(lenderData?.value?.primaryColor || defaultPrimaryColor);
  const secondaryRgb = formatHexToRgb(lenderData?.value?.secondaryColor || defaultSecondaryColor);
  const primaryColor = lenderData?.value?.primaryColor || defaultPrimaryColor;
  const secondaryColor = lenderData?.value?.secondaryColor || defaultSecondaryColor;

  return {
    "--theme-color": primaryColor,
    "--theme-color-rgb": `${themeRgb.r}, ${themeRgb.g}, ${themeRgb.b}`,
    "--secondary-color": secondaryColor,
    "--secondary-color-rgb": `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`,
  };
});

async function getLenderDataByQuery() {
  const query = route.query as any;

  const lenderConfig = await $api.configuration.getLenderConfiguration(
    query?.lid ?? 0,
    query.programTypeId ?? 0,
    query.productTypeId ?? 0,
  );

  await store.dispatch("updateLenderData", lenderConfig);
}

async function handleAppData(appData: any, query: any) {
  if (appData && !appData?.errors) {
    if (appData?.redirectUrl) {
      window.location.href = `${appData?.redirectUrl}`;
    } else {
      // handle storing lead in localStorage until ready to submit to DB
      window.localStorage.setItem("appInProgress", JSON.stringify(appData));
      await store.dispatch("setAppData", appData);
      // Clear out the query parameters
      query.appId = undefined;

      console.log("vuex state this.appData", store.state?.appData);

      const getLenderConfigurationRequest = {
        appId: store.state?.appData?.application?.id,
        lenderId: undefined,
        productTypeId: undefined,
        programTypeId: undefined,
        flowType: flowType.value,
      };

      const lenderConfig = await getLenderConfigurationAsync(getLenderConfigurationRequest);

      await store.dispatch("updateLenderData", lenderConfig);

      delete query?.lid;
      delete query?.continue;
      delete query?.leadSourceId;

      router.replace({ query });

      const applyLoginCookie = getCookie("apply_login");

      if (applyLoginCookie) {
        handleLoginRedirect();
      }
    }
  }
}

async function startNewApplication(): Promise<any> {
  // only performs check on lower environments
  let query = route.query as any;
  console.log("Inside startNewApplication()");

  query = { ...query, flowTypeId: Number(query.flowTypeId) || null, code: query.code || null };

  const appData: AppData = await $api.leads.processLead(query);

  if (appData && appData.flowType) {
    localStorage.setItem("flow", appData.flowType.toString());
  }

  if (appData?.redirectUrl) {
    await handleSilentSignOut();
    window.location.href = `${appData?.redirectUrl}`;
  }

  const leadSourceId = query.leadSourceId || appData?.application?.leadSourceId || 0;
  const productTypeId = query.productTypeId || appData?.application?.productTypeId || 0;
  const programTypeId = query.programTypeId || appData?.application?.programTypeId || 0;
  const flowTypeId = query.flowType || appData?.flowType || null;

  localStorage.setItem("leadSourceId", leadSourceId);
  localStorage.setItem("product", productTypeId);
  localStorage.setItem("program", programTypeId);
  localStorage.setItem("flow", flowTypeId);

  // Sign out users and remove them when starting a new app
  await handleSilentSignOut();

  // Assumed if we get to this point they are starting a new application,
  // so we process any data passed in as a lead
  // Validate that query parameters exist in query
  await getLenderDataByQuery();

  return appData;
}

onMounted(async () => {
  loading.value = true;
  // TODO: Figure out how we are going to get lender information
  const query = route.query as any;
  const appInProgress = window.localStorage.getItem("appInProgress");
  let appData: AppData | undefined;
  console.log("Application Page mounted", { query });
  if (query.appId) {
    console.log("Retrieving application", query.appId);
    // See if the user is trying to pull up existing application
    appData = await $api.applications.getApplication(query.appId, flowType.value);
    console.log("retrievedApp", appData);
  } else if (appInProgress) {
    console.log("Inside localStorage");
    // User may have an application still saved in local storage we can access
    appData = JSON.parse(appInProgress);

    if (appData?.application?.id) {
      appData = await $api.applications.getApplication(appData.application.id, flowType.value);
    }

    console.log("lenderid matches query?.lid", query?.lid !== appData?.application?.lenderId);
    // compare query?.lid to appData.application.lenderId (for invite flow)
    // start new application when incoming query?.lid does not match appInProgress lenderId at any point in application
    if (query?.lid !== undefined && query?.lid !== appData?.application?.lenderId && query.continue !== "true") {
      console.log("clearing localstorage");
      window.localStorage.clear();
      appData = await startNewApplication();
    }
  } else {
    if (query.continue !== "true") {
      appData = await startNewApplication();
    }
  }

  await handleAppData(appData, query);
  loading.value = false;
});

defineExpose({
  props,
});
</script>
<style lang="scss">
.application-page {
  width: 100%;
  height: 100vh;
  display: flex;
  // max-height: 80vh;
  // height: calc(80vh + 1rem);

  height: 81vh;
  display: flex;
  max-height: 100vh;
  .application-ad {
    width: calc(35% - 2em);
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: var(--theme-color);
    color: #fff;
    padding: 0 2em;
    border-right: 20px solid #f0f0f0;

    .cu-logo-wrapper {
      width: 100%;

      .cu-logo {
        width: 125px;
        max-width: 95%;
      }
    }

    h1 {
      font-size: 3.5em;
    }

    .temp-id {
      position: absolute;
      top: 0;
      left: 20px;
    }
  }

  .error-message {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    a {
      display: inline;
    }

    p {
      max-width: 500px;
      font-weight: 700;
    }
  }
}

.cuscd-dark {
  .application-page {
    .application-ad {
      color: #000100;
    }
  }
}

.application-ad-banner-left-nav {
  height: 100%;
  width: calc(4% - 2em);
  background-color: #f0f0f0;
}

.application-ad-banner {
  width: 100%;
  background-color: var(--theme-color);
}

.application-page-footer {
  font-family: var(--body-font);
  font-size: 0.75rem; // font-size: 10px;
  font-weight: 400;
  background-color: #444444;
  color: #ffffff;
  text-align: center;
  border: solid 1px #444444;
  height: 58px;
  vertical-align: baseline;
  /* center content */
  margin: auto;
  display: flex;
  justify-content: center;
  flex-direction: column;
  margin-top: 1rem;
  footer.container {
    text-align: center;
    font-family: var(--body-font);
    font-size: 0.75rem;
    color: #ffffff;

    a {
      color: #ffffff;
    }
    p {
      margin: 0;
    }
  }
}

.application-page.w100 {
  width: 100%;

  .application-steps-wrapper .application-steps .application-steps-wrapper .application-steps {
    width: 100%;
  }
  .application-steps-wrapper {
    width: 75%;
  }
  .application-steps-wrapper .application-steps .application-steps-wrapper .application-steps .application-step {
    overflow-y: auto;
    justify-content: flex-start;
  }
}

.support-wrapper {
  z-index: 2299483676;
  position: absolute;
}

@media (max-width: 992px) {
  .application-page {
    width: 100%;
  }

  .application-page .application-ad,
  .application-page .application-ad-banner-left-nav {
    display: none;
    width: 0;
    opacity: 0;
    visibility: hidden;
  }

  .application-page .application-steps-wrapper {
    width: 100% !important;
  }
}

@media (max-width: 992px) {
  .application-steps-wrapper .application-steps {
    width: 100% !important;
  }
}

@media (max-width: 576px) {
  .application-steps-wrapper .application-steps .application-steps-wrapper .application-steps .application-step {
    overflow-y: auto;
    justify-content: flex-start;
  }

  .application-steps-wrapper .application-steps .application-steps-wrapper .application-steps .step-content {
    max-height: 60vh;
  }
}
</style>
